import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, ArrowUpRight } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Loads /api/features/mine on mount and exposes { features, refresh, isEnabled }.
 *
 * Superadmin sees every feature as enabled (backend already returns True for all).
 * Failing loads default to an EMPTY map so `isEnabled` returns false (fail closed).
 */
const FeaturesContext = createContext(null);

export function FeaturesProvider({ children }) {
    const { user } = useAuth();
    const [features, setFeatures] = useState({});
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!user || user === false) { setFeatures({}); setLoading(false); return; }
        // Candidates don't need this; skip the fetch.
        if (user.role === "candidate") { setFeatures({}); setLoading(false); return; }
        try {
            const r = await api.get("/features/mine");
            setFeatures(r.data?.features || {});
        } catch {
            setFeatures({});
        } finally { setLoading(false); }
    }, [user]);

    useEffect(() => { refresh(); }, [refresh]);

    // Listen for imperative refresh triggers (plan change, override save, billing upgrade).
    // Any code path that mutates the caller's plan or feature overrides should
    // `window.dispatchEvent(new CustomEvent("naukri:features-changed"))` after the
    // successful save so the sidebar / feature gates update without a page reload.
    useEffect(() => {
        const handler = () => refresh();
        window.addEventListener("naukri:features-changed", handler);
        return () => window.removeEventListener("naukri:features-changed", handler);
    }, [refresh]);

    // Cross-user real-time sync via SSE (iter72).
    // When superadmin edits a workspace's plan/features from THEIR device, this
    // stream delivers the change to every live tenant client so their UI
    // refreshes without needing a page reload — even if they're on another laptop.
    const esRef = useRef(null);
    useEffect(() => {
        if (!user || user === false) return;
        if (user.role === "candidate") return; // Candidates don't need feature stream.
        // Close any previous connection when the user identity changes.
        if (esRef.current) { esRef.current.close(); esRef.current = null; }
        try {
            const es = new EventSource(`${BACKEND}/api/events/features`, { withCredentials: true });
            esRef.current = es;
            const onChange = () => refresh();
            es.addEventListener("features-changed", onChange);
            es.addEventListener("settings-changed", () => {
                // Settings (branding) changes → dispatch the tenant event so AppLayout re-fetches too.
                window.dispatchEvent(new CustomEvent("naukri:tenant-branding-changed"));
            });
            // V1.6.0 M4 — DaddyTalk real-time events. Broadcast as window
            // events so any mounted component (Inbox, Launcher badge,
            // Kanban future integrations) can subscribe without spawning
            // a second EventSource (browsers cap ~6 per origin).
            es.addEventListener("daddytalk.thread.created", (ev) => {
                try {
                    window.dispatchEvent(new CustomEvent(
                        "daddytalk:thread-created",
                        { detail: JSON.parse(ev.data || "{}") },
                    ));
                } catch (_) { /* ignore malformed */ }
            });
            es.addEventListener("daddytalk.message.new", (ev) => {
                try {
                    window.dispatchEvent(new CustomEvent(
                        "daddytalk:message-new",
                        { detail: JSON.parse(ev.data || "{}") },
                    ));
                } catch (_) { /* ignore malformed */ }
            });
            // V1.6.3 — Recruiter "just came online" nudge. Bubbles a
            // window event so CandidateMessages (and any future
            // subscriber) can react with a toast.
            es.addEventListener("daddytalk.recruiter.online", (ev) => {
                try {
                    window.dispatchEvent(new CustomEvent(
                        "daddytalk:recruiter-online",
                        { detail: JSON.parse(ev.data || "{}") },
                    ));
                } catch (_) { /* ignore malformed */ }
            });
            // V1.7.4 Sprint 2 — TeamTalk internal staff messages.
            es.addEventListener("teamtalk.message.new", (ev) => {
                try {
                    window.dispatchEvent(new CustomEvent(
                        "teamtalk:message-new",
                        { detail: JSON.parse(ev.data || "{}") },
                    ));
                } catch (_) { /* ignore malformed */ }
            });
            // V1.7.6 Sprint 3 — TeamTalk group updated (rename / member add/remove).
            es.addEventListener("teamtalk.thread.updated", (ev) => {
                try {
                    window.dispatchEvent(new CustomEvent(
                        "teamtalk:thread-updated",
                        { detail: JSON.parse(ev.data || "{}") },
                    ));
                } catch (_) { /* ignore malformed */ }
            });
            // V1.7.7 Sprint 4 — TeamTalk typing indicator + read receipts.
            es.addEventListener("teamtalk.typing", (ev) => {
                try {
                    window.dispatchEvent(new CustomEvent(
                        "teamtalk:typing",
                        { detail: JSON.parse(ev.data || "{}") },
                    ));
                } catch (_) { /* ignore malformed */ }
            });
            es.addEventListener("teamtalk.read", (ev) => {
                try {
                    window.dispatchEvent(new CustomEvent(
                        "teamtalk:read",
                        { detail: JSON.parse(ev.data || "{}") },
                    ));
                } catch (_) { /* ignore malformed */ }
            });
            // V1.8.5 — Reactions on bubbles (TeamTalk + DaddyTalk).
            es.addEventListener("teamtalk.message.reacted", (ev) => {
                try {
                    window.dispatchEvent(new CustomEvent(
                        "teamtalk:message-reacted",
                        { detail: JSON.parse(ev.data || "{}") },
                    ));
                } catch (_) { /* ignore malformed */ }
            });
            es.addEventListener("daddytalk.message.reacted", (ev) => {
                try {
                    window.dispatchEvent(new CustomEvent(
                        "daddytalk:message-reacted",
                        { detail: JSON.parse(ev.data || "{}") },
                    ));
                } catch (_) { /* ignore malformed */ }
            });
            // V1.8.6 — transcript arrives asynchronously after voice upload.
            es.addEventListener("daddytalk.message.updated", (ev) => {
                try {
                    window.dispatchEvent(new CustomEvent(
                        "daddytalk:message-updated",
                        { detail: JSON.parse(ev.data || "{}") },
                    ));
                } catch (_) { /* ignore malformed */ }
            });
            es.addEventListener("teamtalk.message.updated", (ev) => {
                try {
                    window.dispatchEvent(new CustomEvent(
                        "teamtalk:message-updated",
                        { detail: JSON.parse(ev.data || "{}") },
                    ));
                } catch (_) { /* ignore malformed */ }
            });
            // V1.7.9 — DaddyTalk boost changed (Available Now toggle).
            es.addEventListener("daddytalk.boost.changed", (ev) => {
                try {
                    window.dispatchEvent(new CustomEvent(
                        "daddytalk:boost-changed",
                        { detail: JSON.parse(ev.data || "{}") },
                    ));
                } catch (_) { /* ignore malformed */ }
            });

            es.onerror = () => {
                // Silent — EventSource auto-reconnects. Log once to help debugging.
                // console.debug("SSE error, browser will reconnect");
            };
        } catch {
            // EventSource not supported / blocked. Fine — falls back to manual refresh via events.
        }
        return () => {
            if (esRef.current) { esRef.current.close(); esRef.current = null; }
        };
    }, [user, refresh]);

    const isEnabled = useCallback((key) => Boolean(features[key]), [features]);

    return (
        <FeaturesContext.Provider value={{ features, isEnabled, refresh, loading }}>
            {children}
        </FeaturesContext.Provider>
    );
}

export function useFeatures() {
    const ctx = useContext(FeaturesContext);
    if (!ctx) throw new Error("useFeatures must be used inside <FeaturesProvider>");
    return ctx;
}

/**
 * <FeatureGate feature="ai_matching">…children…</FeatureGate>
 *
 * When enabled: renders children.
 * When disabled: renders a soft upsell card (or the `fallback` prop) with an
 *                "Upgrade" CTA linking to /billing.
 * Set `hide` to render nothing when disabled (for nav links / hidden actions).
 */
export function FeatureGate({ feature, children, fallback, hide = false, upgradeLabel = "Upgrade to unlock" }) {
    const { isEnabled, loading } = useFeatures();
    if (loading) return null;
    if (isEnabled(feature)) return <>{children}</>;
    if (hide) return null;
    if (fallback !== undefined) return <>{fallback}</>;
    return (
        <div
            data-testid={`feature-locked-${feature}`}
            className="bg-white border border-dashed border-[#E5E5E5] p-6 text-center"
        >
            <Lock size={20} weight="regular" className="text-[#A3A3A3] mx-auto mb-2" />
            <p className="text-sm text-[#525252] mb-3">
                This feature isn&apos;t available on your current plan.
            </p>
            <Link
                to="/billing"
                data-testid={`feature-upgrade-${feature}`}
                className="btn-gradient inline-flex items-center gap-1 px-4 py-2 text-xs font-black uppercase tracking-wider"
            >
                {upgradeLabel} <ArrowUpRight size={12} weight="bold" />
            </Link>
        </div>
    );
}
